import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, Check, Shuffle, Sparkles } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getAsset } from '@/api/assets'
import { ApiError } from '@/api/client'
import { CastSuggestionResult, startCastSuggestion, type CastSuggestionRequest } from '@/api/castSuggestions'
import { cancelJob, getJob } from '@/api/jobs'
import { keys } from '@/api/keys'
import { preflightStory } from '@/api/lessons'
import { puppetsQuery, type PuppetKind } from '@/api/puppets'
import { getSprite } from '@/api/sprites'
import { createStory, storyPresetsQuery, worldLabel, worldPlatesQuery, type AgeBand, type StoryCastRequest, type StoryLength, type StoryRequest } from '@/api/stories'
import { Button } from '@/components/ui/button'
import { Field, Input, Textarea } from '@/components/ui/input'
import { cn } from '@/components/ui/utils'
import { pickFromSprite, SpriteCastPicker } from '@/features/sprites/SpriteCastPicker'
import type { StoryGenerationMode } from '@/schema/settings'

import { CastPicker, CastReasons, MAX_CAST, pickFromFeed, type CastPick } from './CastPicker'
import { LengthPicker } from './LengthPicker'
import { NewMovesHint, SceneryToggle, ThingsField, useIdeaThings, useNewMoves } from './StoryThings'
import { mergeThings, type ThingPick } from './things'

/**
 * `/stories/new` — three small steps: **Who's in the story?** (1–3 puppets), **Where does it
 * happen?** (a place, or "Surprise me"), **What happens?** (any idea, the things in it, the new
 * moves the studio will learn for it, the living background switch and the listener's age). "Make
 * the story" posts `POST /api/stories` (§3.4, Q10) and lands on the story page, which shows the
 * real progress and starts the player by itself when it is ready. Every story is a free story:
 * the studio follows the idea wherever it goes and makes it move (`backend/app/story/motion.py`).
 */

export interface StoryWizardProps {
  initialAuthoringVersion?: 'events@1'
  initialCast?: string[]
  initialWorld?: string
  initialBand?: AgeBand
  initialIdea?: string
  initialLength?: StoryLength
  initialGenerationMode?: StoryGenerationMode
}

const STEPS = [
  { n: 1, title: 'Who’s in the story?', short: 'Who' },
  { n: 2, title: 'Where does it happen?', short: 'Where' },
  { n: 3, title: 'What happens?', short: 'What' },
] as const
type Step = 1 | 2 | 3

const BANDS: Array<{ band: AgeBand; label: string }> = [
  { band: '3-4', label: 'Ages 3–4' },
  { band: '5-6', label: 'Ages 5–6' },
  { band: '7-8', label: 'Ages 7–8' },
]

export const IDEAS = [
  'A little bunny hops through the meadow, and a red balloon floats away into the sky.',
  'Two friends dance in the park until soft snow starts to fall.',
  'A sleepy kitten chases fireflies under the twinkling stars.',
  'The wind blows a hat away, and everyone dashes after it!',
  'A rainy day turns into a rainbow party.',
  'A tiny seed grows into a giant glowing flower.',
]
const IDEA_HINT = 'Any idea: a game, a little journey, magic, weather, music, a silly mix-up. The studio makes it move.'
const IDEA_PLACEHOLDER = 'A kite flies up to the clouds, and a bird joins the dance…'

/** How many friends may talk for an age band (the rest listen). Defaults match the seed table. */
const DEFAULT_TALKERS: Record<AgeBand, number> = { '3-4': 2, '5-6': 3, '7-8': 4 }

/** The cast as sent: first pick is the hero; talkers beyond the band's limit only listen. */
export function castRequest(picks: CastPick[], maxTalkers: number): StoryCastRequest[] {
  let talking = 0
  return picks.map((p, i) => {
    const speaks = p.speaks && talking < maxTalkers
    if (speaks) talking += 1
    return { uid: p.uid, role: i === 0 ? 'hero' : 'friend', speaks }
  })
}

export function StoryWizard({ initialCast = [], initialWorld, initialBand, initialIdea, initialLength, initialGenerationMode, initialAuthoringVersion }: StoryWizardProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const presets = useQuery(storyPresetsQuery())
  const [generationMode, setGenerationMode] = useState<StoryGenerationMode | null>(initialGenerationMode ?? null)
  const [step, setStep] = useState<Step>(1)
  const [picks, setPicks] = useState<CastPick[]>(() => initialCast.map((uid) => placeholder(uid)))
  const [world, setWorld] = useState(initialWorld ?? '')
  const [idea, setIdea] = useState(initialIdea ?? '')
  const eventGeneration = presets.data?.event_generation_enabled === true || initialAuthoringVersion === 'events@1'
  const ideas = IDEAS
  const [band, setBand] = useState<AgeBand>(initialBand ?? '3-4')
  const [listener, setListener] = useState('')
  // Things in the story (Q10): found in the idea, minus the removed ones, plus the added ones.
  const [addedThings, setAddedThings] = useState<ThingPick[]>([])
  const [removedThings, setRemovedThings] = useState<ReadonlySet<string>>(() => new Set())
  const [scenery, setScenery] = useState(true)
  // How long (Short / Medium / Long); only a longer story is sent (the server's default is short).
  const [length, setLength] = useState<StoryLength>(initialLength ?? 'short')
  const [aiCast, setAiCast] = useState(false)
  const [castJob, setCastJob] = useState<{ id: number; key: string } | null>(null)
  const [review, setReview] = useState<{ key: string; model: string } | null>(null)
  const [castResultError, setCastResultError] = useState<string | null>(null)
  const castingRequest: CastSuggestionRequest = { prompt: idea.trim(), age_band: band, generation_mode: generationMode ?? 'bones', ...(world ? { world } : {}) }
  const castingKey = JSON.stringify(castingRequest)
  const castingAttempt = useRef(0)
  const activeCasting = useRef({ key: castingKey, enabled: aiCast })
  activeCasting.current = { key: castingKey, enabled: aiCast }
  const suggest = useMutation({
    mutationFn: ({ body }: { body: CastSuggestionRequest; attempt: number }) => startCastSuggestion(body),
    onSuccess: ({ job_id }, { body, attempt }) => {
      const key = JSON.stringify(body)
      if (attempt === castingAttempt.current && activeCasting.current.enabled && activeCasting.current.key === key) setCastJob({ id: job_id, key })
      else void cancelJob(job_id).catch(() => undefined)
    },
  })
  const currentCastJob = aiCast && castJob?.key === castingKey ? castJob : null
  const castProgress = useQuery({
    queryKey: ['story-cast', currentCastJob?.id],
    queryFn: ({ signal }) => getJob(currentCastJob!.id, signal),
    enabled: currentCastJob !== null,
    retry: false,
    refetchInterval: (query) => ['succeeded', 'failed', 'cancelled'].includes(query.state.data?.status ?? '') ? false : 1000,
  })
  const reviewed = aiCast && review?.key === castingKey
  const castingBusy = suggest.isPending || (currentCastJob !== null && !castProgress.isError && !['succeeded', 'failed', 'cancelled'].includes(castProgress.data?.status ?? ''))
  useEffect(() => {
    if (!currentCastJob || castProgress.data?.status !== 'succeeded' || review?.key === currentCastJob.key) return
    const parsed = CastSuggestionResult.safeParse(castProgress.data.result)
    if (!parsed.success || parsed.data.generation_mode !== generationMode) {
      setCastResultError('The suggested cast could not be read. Try again or choose characters yourself.')
      return
    }
    setPicks(parsed.data.cast.map((pick) => ({ uid: pick.uid, name: pick.name, thumb: pick.thumb_url, kind: pick.kind, speaks: pick.speaks, learning: false, reason: pick.reason })))
    setReview({ key: currentCastJob.key, model: parsed.data.model })
  }, [currentCastJob, castProgress.data, generationMode, review])
  // Results from an old idea or a cancelled request cannot overwrite a later manual choice.
  useEffect(() => {
    if (castJob && (!aiCast || castJob.key !== castingKey)) {
      void cancelJob(castJob.id).catch(() => undefined)
      setCastJob(null)
    }
  }, [aiCast, castingKey, castJob])
  const requestCast = (): void => {
    const attempt = ++castingAttempt.current
    setCastJob(null)
    setReview(null)
    setCastResultError(null)
    suggest.mutate({ body: castingRequest, attempt })
  }
  const chooseManually = (): void => {
    // Cancelling retires the attempt even if the next draft has identical inputs.
    castingAttempt.current += 1
    suggest.reset()
    activeCasting.current.enabled = false
    setAiCast(false)
    setCastJob(null)
    setReview(null)
    if (castJob && castingBusy) void cancelJob(castJob.id).catch(() => undefined)
    setStep(1)
  }
  const castError = castResultError ?? (suggest.error instanceof Error ? suggest.error.message : null)
    ?? (currentCastJob && castProgress.isError ? 'Cast progress could not be loaded. Try again or choose characters yourself.' : null)
    ?? (currentCastJob && castProgress.data?.status === 'failed' ? castProgress.data.error ?? 'The cast could not be suggested. Try again.' : null)
    ?? (currentCastJob && castProgress.data?.status === 'cancelled' ? 'Casting was cancelled. You can try again.' : null)

  // Freeze the mode for this draft; a settings refresh must not reinterpret already picked uids.
  useEffect(() => {
    if (generationMode === null && presets.isSuccess) setGenerationMode(presets.data.generation_mode ?? 'bones')
  }, [generationMode, presets.isSuccess, presets.data])
  useEffect(() => {
    if (generationMode === 'spritesheet') setPicks((cur) => cur.filter((pick) => !pick.uid.startsWith('skel:')))
  }, [generationMode])
  usePrefillNames(picks, setPicks, generationMode)
  const sprites = generationMode === 'spritesheet'
  const castNames = picks.filter((p) => !p.pending).map((p) => p.name)
  const ideaThings = useIdeaThings(idea, castNames)
  const moves = useNewMoves(generationMode === 'bones' ? idea : '', castNames, generationMode === 'bones')
  const things = mergeThings(ideaThings.found, addedThings, removedThings)
  // Things are the AI's to pick while it writes the story; only a hand edit (one added or removed)
  // makes them the parent's choice, sent as they are.
  const thingsChosen = addedThings.length > 0 || removedThings.size > 0
  const props = thingsChosen ? things.map((t) => ({ uid: t.uid })) : undefined
  const removeThing = (t: ThingPick): void => {
    setRemovedThings((cur) => new Set(cur).add(t.uid))
    setAddedThings((cur) => cur.filter((a) => a.uid !== t.uid))
  }
  const addThing = (t: ThingPick): void => {
    setRemovedThings((cur) => (cur.has(t.uid) ? new Set([...cur].filter((u) => u !== t.uid)) : cur))
    setAddedThings((cur) => (cur.some((a) => a.uid === t.uid) ? cur : [...cur, t]))
  }

  const maxTalkers = presets.data?.age_bands.find((b) => b.band === band)?.max_cast ?? DEFAULT_TALKERS[band]
  const cast = castRequest(picks, maxTalkers)
  const quiet = picks.filter((p, i) => p.speaks && !cast[i]?.speaks)

  const toggle = (pick: CastPick): void => {
    if (step === 1 && aiCast) chooseManually()
    setPicks((cur) => (cur.some((p) => p.uid === pick.uid) ? cur.filter((p) => p.uid !== pick.uid) : cur.length < MAX_CAST ? [...cur, pick] : cur))
  }

  // Characters are optional: with none picked, the AI casts the idea when the story is made.
  const autoCast = picks.length === 0
  const castReady = generationMode !== null && (autoCast || !sprites || picks.every((p) => !p.pending && !p.uid.startsWith('skel:')))
  const draftReady = castReady && idea.trim().length >= 3 && (!aiCast || autoCast || reviewed)
  const chosenProps = props
  const creationRequest: StoryRequest = { prompt: idea.trim(), age_band: band, generation_mode: generationMode ?? 'bones', world: world || undefined, cast, listener_name: listener.trim() || undefined, ...(chosenProps ? { props: chosenProps } : {}), scenery, ...(length !== 'short' ? { length } : {}) }
  const requestKey = JSON.stringify(creationRequest)
  const [settledRequest, setSettledRequest] = useState(requestKey)
  useEffect(() => { const timer = setTimeout(() => setSettledRequest(requestKey), 250); return () => clearTimeout(timer) }, [requestKey])
  const preview = useQuery({ queryKey: ['story-preflight', settledRequest], queryFn: ({ signal }) => preflightStory(JSON.parse(settledRequest) as StoryRequest, signal),
    enabled: eventGeneration && step === 3 && draftReady && settledRequest === requestKey, retry: false, staleTime: 0 })
  const previewReady = !eventGeneration || (settledRequest === requestKey && preview.isSuccess && !preview.isFetching && preview.data.status === 'ready')
  const make = useMutation({
    mutationFn: () => {
      if (!previewReady) throw new Error('Wait for the story preflight before generating.')
      return createStory({ ...creationRequest, ...(eventGeneration ? { authoring_version: 'events@1' as const } : {}) })
    },
    onSuccess: ({ story }) => {
      void qc.invalidateQueries({ queryKey: keys.stories.all() })
      void navigate({ to: '/stories/$uid', params: { uid: story.uid }, search: { step: 'generate' } })
    },
  })

  const canNext = step === 1 ? castReady : true
  const canMake = draftReady && previewReady && presets.isSuccess
  const nextHint = step === 1 && picks.length === 0 ? 'No one picked: AI will choose the characters from your idea' : null

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8" data-testid="story-wizard">
      <header className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-ink">Make a story</h1>
          <p className="mt-1 max-w-2xl text-ink-2">Pick a few friends, a place and a little idea. The studio writes a short story and your puppets act it out.</p>
          {generationMode ? (
            <p className="mt-3 text-sm text-ink-2" data-testid="generation-mode">
              {sprites ? 'Sprite sheets · Cast from ready sprite clips.' : 'Bones · Cast from puppets and the picture catalog.'}{' '}
              {initialGenerationMode ? 'Mode from your story link.' : 'Default from Settings.'}{' '}
              <Link to="/settings" search={{ tab: 'story' }} className="underline underline-offset-4 focus-visible:focus-ring">Story settings</Link>
            </p>
          ) : null}
        </div>
        <ol className="flex flex-wrap items-center gap-2" aria-label="Steps">
          {STEPS.map((s) => {
            const done = s.n < step
            const current = s.n === step
            const reachable = s.n <= step || (s.n > 1 && (castReady || aiCast))
            return (
              <li key={s.n} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => reachable && setStep(s.n)}
                  disabled={!reachable}
                  aria-current={current ? 'step' : undefined}
                  className={cn(
                    'inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors focus-visible:focus-ring disabled:opacity-50',
                    current ? 'border-transparent bg-accent text-accent-ink' : done ? 'border-transparent bg-accent-soft text-ink' : 'border-line text-ink-2 hover:bg-paper-2',
                  )}
                >
                  <span className={cn('inline-flex size-6 items-center justify-center rounded-full text-xs', current ? 'bg-white/25' : 'bg-paper')}>
                    {done ? <Check className="size-3.5" aria-hidden /> : s.n}
                  </span>
                  {s.short}
                </button>
                {s.n < 3 ? <span className="h-px w-4 bg-line-strong" aria-hidden /> : null}
              </li>
            )
          })}
        </ol>
      </header>

      <section className="card flex flex-col gap-5 p-4 sm:p-6" aria-labelledby="wizard-step-title">
        <h2 id="wizard-step-title" className="text-2xl font-semibold text-ink">
          {STEPS[step - 1]?.title}
        </h2>

        {step === 1 ? (
          <>
            <p className="-mt-3 text-sm text-ink-2">Optional: pick up to {MAX_CAST} friends — the first one is the hero. Pick none and AI chooses them from your idea.</p>
            {generationMode !== null ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line p-3">
                <p className="min-w-48 flex-1 text-sm text-ink-2">Have an idea first? AI chooses ready characters from your library when it writes the story — the child your idea names, a friend to play with.</p>
                <Button variant="secondary" onClick={() => { if (aiCast) chooseManually(); setPicks([]); setStep(2) }}><Sparkles aria-hidden />Let AI choose</Button>
              </div>
            ) : null}
            {generationMode === null ? presets.isError ? (
              <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-danger">
                <p>Story settings could not be loaded.</p>
                <Button variant="secondary" onClick={() => void presets.refetch()}>Try again</Button>
              </div>
            ) : <p role="status" className="text-sm text-ink-2">Loading story settings…</p> : sprites ? <SpriteCastPicker picks={picks} onToggle={toggle} /> : <CastPicker picks={picks} onToggle={toggle} />}
            {sprites && initialCast.some((uid) => uid.startsWith('skel:')) ? <p role="status" className="text-sm text-ink-2">Your linked bone puppets are not available in sprite sheet mode. Choose sprite characters above, or change the mode in Story settings.</p> : null}
          </>
        ) : step === 2 ? (
          <WorldPicker value={world} onChange={setWorld} />
        ) : (
          <div className="flex flex-col gap-5">
            <Field label="The idea" htmlFor="story-idea">
              <Textarea id="story-idea" value={idea} onChange={(e) => setIdea(e.target.value)} rows={4} maxLength={1200} required aria-describedby="story-idea-hint" placeholder={IDEA_PLACEHOLDER} />
              <p id="story-idea-hint" className="text-xs text-ink-2">{IDEA_HINT}</p>
            </Field>
            {aiCast ? <p className="text-sm text-ink-2">AI will suggest characters from the {sprites ? 'ready sprite' : 'ready puppet'} library for this idea. <button type="button" className="underline underline-offset-4 focus-visible:focus-ring" onClick={chooseManually}>Choose characters myself</button></p> : null}
            <div className="flex flex-wrap gap-2" aria-label="Ideas to start from">
              {ideas.map((text) => (
                <button
                  key={text}
                  type="button"
                  aria-pressed={idea === text}
                  onClick={() => setIdea(text)}
                  className="min-h-10 max-w-full rounded-2xl border border-line bg-paper px-3 py-2 text-left text-sm text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:focus-ring aria-pressed:border-transparent aria-pressed:bg-accent-soft aria-pressed:text-ink"
                >
                  {text}
                </button>
              ))}
            </div>
            <p className="text-sm text-ink-2" data-testid="things-auto">{thingsChosen ? 'You chose the things in this story.' : 'AI picks the things your story needs while it writes it. Add or remove one to choose them yourself.'}</p>
            <ThingsField things={things} looking={ideaThings.looking} onRemove={removeThing} onAdd={addThing} />
            {sprites ? <p className="text-sm text-ink-2">Characters use their original sprite animations. Things and scenery use bone animation so characters can carry, share, toss, and move them.</p> : null}
            {!sprites ? <NewMovesHint moves={moves} /> : null}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-ink" id="age-label">
                Who is listening?
              </span>
              <div role="radiogroup" aria-labelledby="age-label" className="grid max-w-md grid-cols-3 gap-2">
                {BANDS.map((b) => (
                  <button
                    key={b.band}
                    type="button"
                    role="radio"
                    aria-checked={band === b.band}
                    onClick={() => setBand(b.band)}
                    className={cn(
                      'h-12 rounded-xl border text-sm font-semibold transition-colors focus-visible:focus-ring',
                      band === b.band ? 'border-transparent bg-accent text-accent-ink' : 'border-line bg-paper text-ink hover:bg-paper-2',
                    )}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              {quiet.length > 0 ? (
                <p className="text-xs text-ink-2" data-testid="quiet-note">
                  For {BANDS.find((b) => b.band === band)?.label.toLowerCase()}, up to {maxTalkers} friends talk. {quiet.map((p) => p.name).join(' and ')} will listen.
                </p>
              ) : null}
            </div>
            <LengthPicker value={length} onChange={setLength} lengths={presets.data?.lengths} />
            {aiCast && castingBusy ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl bg-paper-2 p-3">
                <p role="status" className="flex-1 text-sm text-ink-2">{castProgress.data?.progress.msg ?? 'Asking AI to choose your cast…'}</p>
                <Button variant="secondary" onClick={chooseManually}>Cancel casting</Button>
              </div>
            ) : null}
            {aiCast && castError ? <p role="alert" className="rounded-xl bg-danger-soft p-3 text-sm text-danger">{castError}</p> : null}
            {reviewed ? (
              <section aria-label="Review your cast" className="flex flex-col gap-4 rounded-xl border border-line p-4">
                <div>
                  <h3 className="text-xl font-semibold text-ink">Review your cast</h3>
                  <p className="mt-1 text-sm text-ink-2">Keep these friends, remove one, or choose others below. The first is the hero.</p>
                  <p className="mt-1 text-xs text-ink-3">Suggested by {review.model}</p>
                </div>
                <CastReasons picks={picks} onHero={(uid) => setPicks((current) => [...current.filter((pick) => pick.uid === uid), ...current.filter((pick) => pick.uid !== uid)])} />
                {sprites ? <SpriteCastPicker picks={picks} onToggle={toggle} /> : <CastPicker picks={picks} onToggle={toggle} />}
                <Button variant="secondary" className="self-start" onClick={requestCast} disabled={castingBusy}>Suggest another cast</Button>
              </section>
            ) : null}
            <SceneryToggle checked={scenery} onChange={setScenery} />
            {eventGeneration && draftReady && <section aria-label="Story preflight" className="space-y-3 rounded-xl border border-line p-4">
              <h3 className="font-semibold text-ink">Before we make the story</h3>
              {preview.isPending || preview.isFetching || settledRequest !== requestKey ? <p role="status">Checking the characters and objects…</p> : preview.isError ? <p role="alert">Preflight could not load. {preview.error instanceof Error ? preview.error.message : 'Try again.'}</p> : preview.data && <>
                <p role="status">{preview.data.status === 'ready' ? 'Ready to generate. The finished story will be checked again.' : 'This story needs a change before generation.'}</p>
                {preview.data.notices.map((notice, i) => <p key={i} className="text-sm text-ink-2">{notice}</p>)}
                {preview.data.cast_issues.map((issue, i) => <p key={i} className="text-sm text-danger">{picks.find((p) => p.uid === issue.uid)?.name}: {issue.message}</p>)}
                <p className="text-sm font-medium">{autoCast ? 'Characters: AI chooses them from your idea. ' : ''}Objects included: {!thingsChosen ? 'AI picks them while it writes the story' : preview.data.props.length ? preview.data.props.map((p) => `${p.name}${p.count > 1 ? ` × ${p.count}` : ''}`).join(', ') : 'None'}</p>
                {thingsChosen && preview.data.props.length > 0 && <Button variant="secondary" onClick={() => { setAddedThings([]); setRemovedThings(new Set([...removedThings, ...preview.data!.props.map((p) => p.uid), ...things.map((p) => p.uid)])) }}>Use no objects</Button>}
              </>}
              <Button variant="secondary" disabled={preview.isFetching || settledRequest !== requestKey} onClick={() => void preview.refetch()}>Check again</Button>
            </section>}
            <details className="group rounded-xl border border-line p-3">
              <summary className="cursor-pointer text-sm font-medium text-ink">More options</summary>
              <Field label="The listener’s name" htmlFor="story-listener" hint="Optional — the story can say goodnight to them." className="mt-3 max-w-sm">
                <Input id="story-listener" value={listener} maxLength={60} onChange={(e) => setListener(e.target.value)} />
              </Field>
            </details>
            {make.error ? (
              <p role="alert" className="rounded-xl bg-danger-soft p-3 text-sm text-danger">
                {make.error instanceof Error ? make.error.message : 'The story could not be started.'}
              </p>
            ) : null}
          </div>
        )}
      </section>

      {/* Stays above the queue strip (AppShell publishes its height as --shell-bottom). */}
      <div className="sticky z-10 -mx-2 flex items-center gap-3 rounded-2xl border border-line bg-paper/95 p-2 shadow-pop backdrop-blur" style={{ bottom: 'calc(var(--shell-bottom, 40px) + 0.5rem)' }}>
        {step > 1 ? (
          <Button variant="ghost" size="lg" onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}>
            <ArrowLeft aria-hidden />
            Back
          </Button>
        ) : null}
        <p className="min-w-0 flex-1 truncate text-sm text-ink-2" aria-live="polite">
          {nextHint ?? (picks.length ? `With ${picks.map((p) => p.name).join(', ')}` : '')}
        </p>
        {step < 3 ? (
          <Button variant="primary" size="lg" disabled={!canNext} onClick={() => setStep((s) => (s < 3 ? ((s + 1) as Step) : s))}>
            Next
            <ArrowRight aria-hidden />
          </Button>
        ) : aiCast && !reviewed ? (
          <Button variant="primary" size="lg" disabled={generationMode === null || idea.trim().length < 3 || castingBusy} loading={castingBusy} onClick={requestCast}>
            {castingBusy ? null : <Sparkles aria-hidden />}
            Suggest a cast
          </Button>
        ) : (
          <Button variant="primary" size="lg" disabled={!canMake} loading={make.isPending} onClick={() => make.mutate()}>
            {make.isPending ? null : <Sparkles aria-hidden />}
            Make the story
          </Button>
        )}
      </div>
    </div>
  )
}

function placeholder(uid: string): CastPick {
  return { uid, name: '…', thumb: null, kind: null, speaks: true, learning: !uid.startsWith('skel:'), pending: true }
}

/** Fill in the name and picture of puppets that arrived through `?cast=`. */
function usePrefillNames(picks: CastPick[], setPicks: (fn: (cur: CastPick[]) => CastPick[]) => void, generationMode: StoryGenerationMode | null): void {
  const unresolved = picks.filter((p) => p.pending)
  const needsFeed = generationMode === 'bones' && unresolved.some((p) => p.uid.startsWith('skel:'))
  const feed = useQuery({ ...puppetsQuery({}), enabled: needsFeed })
  const spriteUids = generationMode === 'spritesheet' ? unresolved.filter((p) => !p.uid.startsWith('skel:')).map((p) => p.uid) : []
  const sprites = useQueries({ queries: spriteUids.map((uid) => ({
    queryKey: ['sprites', 'detail', uid],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try { return await getSprite(uid, signal) }
      catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 422)) return null
        throw error
      }
    },
    retry: false,
    staleTime: 60_000,
  })) })
  const spriteKey = sprites.map((sprite, i) => `${spriteUids[i]}:${sprite.status}`).join('|')
  const assetUids = generationMode === 'bones' ? unresolved.filter((p) => !p.uid.startsWith('skel:')).map((p) => p.uid) : []
  const assets = useQueries({ queries: assetUids.map((uid) => ({ queryKey: keys.assets.detail(uid), queryFn: ({ signal }: { signal: AbortSignal }) => getAsset(uid, signal), retry: false, staleTime: 60_000 })) })
  const assetKey = assets.map((a) => (a.data ? a.data.uid : a.isError ? 'x' : '')).join('|')

  useEffect(() => {
    if (unresolved.length === 0) return
    if (generationMode === 'spritesheet') {
      const settled = new Map<string, CastPick | null>()
      sprites.forEach((sprite, i) => {
        if (sprite.isSuccess) settled.set(spriteUids[i]!, sprite.data?.ready ? pickFromSprite(sprite.data) : null)
      })
      if (settled.size === 0) return
      // Only authoritative unavailability removes a link; loading/network failures keep it pending.
      setPicks((cur) => cur.flatMap((pick) => {
        if (!pick.pending || !settled.has(pick.uid)) return [pick]
        const found = settled.get(pick.uid)
        return found ? [found] : []
      }))
      return
    }
    if (generationMode !== 'bones') return
    const byUid = new Map<string, CastPick>()
    for (const p of feed.data?.items ?? []) byUid.set(p.uid, pickFromFeed(p))
    for (const a of assets) {
      if (!a.data) continue
      const kind: PuppetKind = a.data.kind === 'object' ? 'object' : 'person'
      byUid.set(a.data.uid, { uid: a.data.uid, name: a.data.name, thumb: a.data.media?.thumb ?? null, kind, speaks: a.data.kind !== 'object', learning: true })
    }
    const failed = (uid: string): boolean => (uid.startsWith('skel:') ? feed.isSuccess || feed.isError : assets[assetUids.indexOf(uid)]?.isError === true)
    if (byUid.size === 0 && !unresolved.some((p) => failed(p.uid))) return
    setPicks((cur) =>
      cur.map((p) => {
        if (!p.pending) return p
        const found = byUid.get(p.uid)
        if (found) return found
        if (!failed(p.uid)) return p
        return { ...p, pending: false, name: p.uid.startsWith('skel:') ? 'Your puppet' : (p.uid.split('/').pop() ?? 'Friend') }
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generationMode, feed.data, feed.isError, assetKey, spriteKey])
}

/* -------------------------------------------------------------------------- */
/* Where                                                                       */
/* -------------------------------------------------------------------------- */

function WorldPicker({ value, onChange }: { value: string; onChange(world: string): void }) {
  const presets = useQuery(storyPresetsQuery())
  const plates = useQuery(worldPlatesQuery())
  const worlds = useMemo(() => (presets.data?.worlds ?? []).filter((w) => w.stages_ready > 0), [presets.data])
  const options = [{ world: '', label: 'Surprise me' }, ...worlds.map((w) => ({ world: w.world, label: worldLabel(w.world) }))]
  return (
    <div role="radiogroup" aria-label="Places" className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-3" data-testid="world-picker">
      {options.map((o) => {
        const on = value === o.world
        const src = o.world ? plates.data?.[o.world] : undefined
        return (
          <button
            key={o.world || 'surprise'}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.world)}
            className={cn(
              'group relative flex flex-col overflow-hidden rounded-2xl border bg-paper text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-pop focus-visible:focus-ring',
              on ? 'border-accent ring-2 ring-accent' : 'border-line',
            )}
          >
            <span className="relative flex aspect-video items-center justify-center overflow-hidden bg-paper-2">
              {src ? (
                <img src={src} alt="" loading="lazy" className="size-full object-cover" />
              ) : o.world ? null : (
                <Shuffle className="size-8 text-accent" aria-hidden />
              )}
              {on ? (
                <span className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-full bg-accent text-accent-ink shadow-card" aria-hidden>
                  <Check className="size-4" />
                </span>
              ) : null}
            </span>
            <span className="px-3 py-2.5 text-sm font-semibold text-ink">{o.label}</span>
          </button>
        )
      })}
      {presets.isPending ? <p className="col-span-full text-sm text-ink-3">Finding places…</p> : null}
    </div>
  )
}
